'use client';

import { useEffect, useRef, useState } from 'react';
import { Box, Text, Group } from '@mantine/core';
import { IconSpeakerphone, IconArrowRight } from '@tabler/icons-react';
import Link from 'next/link';

// In-feed ad slot between bag cards on the set page.
//
// Two modes:
// - AdSense configured (NEXT_PUBLIC_ADSENSE_CLIENT + NEXT_PUBLIC_ADSENSE_SLOT_INFEED):
//   renders a labeled Google in-article ad, lazy-loaded near the viewport so
//   viewability (and CPM) stays high, with reserved height so the page never
//   jumps (no CLS).
// - Not (yet) configured: renders a house ad inviting advertisers, linking to
//   /advertise. This keeps the spot useful while waiting for AdSense approval.

const CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT;
const DEFAULT_SLOT = process.env.NEXT_PUBLIC_ADSENSE_SLOT_INFEED;

function HouseAd() {
  return (
    <Box
      component={Link}
      href="/advertise"
      my={10}
      style={{ display: 'block', textDecoration: 'none' }}
    >
      <Group
        justify="center"
        gap="md"
        px="md"
        py="md"
        wrap="wrap"
        className="house-ad-card"
        style={{
          backgroundColor: '#fcfcfc',
          border: '1.5px dashed #ced4da',
          borderRadius: 12,
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
        }}
      >
        <Box
          className="feature-icon-bubble"
          style={{ backgroundColor: 'var(--bpb-blue-soft)', width: 40, height: 40, marginBottom: 0, borderRadius: 12, flexShrink: 0 }}
        >
          <IconSpeakerphone size={22} color="#1971c2" stroke={1.7} />
        </Box>
        <Box style={{ textAlign: 'center' }}>
          <Text size="sm" fw={700} style={{ color: 'var(--bpb-text)' }}>
            This spot is available
          </Text>
          <Text size="xs" c="dimmed">
            Reach LEGO builders, collectors and resellers
          </Text>
        </Box>
        <Group gap={4} style={{ color: 'var(--bpb-blue)', flexShrink: 0 }}>
          <Text size="sm" fw={600} style={{ color: 'inherit' }}>Advertise with us</Text>
          <IconArrowRight size={16} />
        </Group>
      </Group>
    </Box>
  );
}

export function AdSlot({ slot = DEFAULT_SLOT, minHeight = 130 }) {
  const boxRef = useRef(null);
  const pushed = useRef(false);
  const [nearViewport, setNearViewport] = useState(false);
  const configured = Boolean(CLIENT && slot);

  useEffect(() => {
    if (!configured) return;
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setNearViewport(true);
        obs.disconnect();
      }
    }, { rootMargin: '400px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [configured]);

  useEffect(() => {
    if (!nearViewport || !configured || pushed.current) return;
    pushed.current = true;
    // The loader script is injected by the root layout (next/script,
    // lazyOnload); pushes queue in window.adsbygoogle until it loads.
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) { /* ad blocker */ }
  }, [nearViewport, configured]);

  if (!configured) return <HouseAd />;

  return (
    <Box
      ref={boxRef}
      my={10}
      px="md"
      py="xs"
      style={{
        backgroundColor: '#fcfcfc',
        border: '1px solid #e9ecef',
        borderRadius: 12,
      }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ fontSize: 9, letterSpacing: 0.5 }} mb={4}>
        Advertisement
      </Text>
      {nearViewport && (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', minHeight }}
          data-ad-client={CLIENT}
          data-ad-slot={slot}
          data-ad-format="fluid"
          data-ad-layout="in-article"
        />
      )}
    </Box>
  );
}
